/*
 * As regras de escolha de tags do flarum/tags, para os seletores próprios do
 * tema (a linha "tag primária | tags secundárias" do composer, as sugestões
 * da IA, o tópico vinculado). São as mesmas do TagDiscussionModal e da
 * validação do servidor (`DiscussionResourceFields`):
 *
 * - só entram as tags de `getSelectableTags`: as que o ator pode usar para
 *   iniciar discussão ou, editando uma discussão, as que pode adicionar a ela
 *   (mais as que ela já tem);
 * - uma tag filha só vale com a tag pai escolhida (`requireParentTag`); sem o
 *   pai, as filhas dele saem junto;
 * - "primária" é a primária de primeiro nível (`isPrimaryParent`); as filhas
 *   contam como secundárias, como o servidor conta;
 * - mínimos e máximos de `flarum-tags.*` (`minPrimaryTags` etc.), com a
 *   permissão "ignorar requisitos de tags" (`canBypassTagCounts`) dispensando
 *   os mínimos, como o modal do core permite publicar sem eles.
 *
 * Sempre que o flarum/tags expõe a função (`getSelectableTags`, `sortTags`),
 * ela é usada no lugar da cópia daqui, para seguir o comportamento dele.
 */
import app from 'flarum/forum/app';
import { allTags, isTagsEnabled, sortTags as themeSortTags } from '../utils/tags';
/** Os padrões da migration do flarum/tags, para quando o atributo falta. */
const DEFAULTS = {
  minPrimaryTags: 1,
  maxPrimaryTags: 1,
  minSecondaryTags: 0,
  maxSecondaryTags: 3
};
function setting(name) {
  const value = parseInt(String(app.forum.attribute(name) ?? ''), 10);
  return Number.isFinite(value) && value >= 0 ? value : DEFAULTS[name];
}
export function tagLimits() {
  return {
    minPrimary: setting('minPrimaryTags'),
    maxPrimary: setting('maxPrimaryTags'),
    minSecondary: setting('minSecondaryTags'),
    maxSecondary: setting('maxSecondaryTags'),
    bypass: !!app.forum.attribute('canBypassTagCounts')
  };
}

/** O pai de uma filha (pelo atributo `isChild`, que vem sempre). */
export function parentTag(tag) {
  if (!tag.isChild()) return null;
  const parent = tag.parent();
  return parent ? parent : null;
}

/** Primária de primeiro nível: a única que conta como "primária" no servidor. */
export function isPrimaryParent(tag) {
  return tag.position() !== null && tag.position() !== undefined && !tag.isChild();
}

/** Secundária "de verdade" (sem posição): a que fica no seletor da direita. */
export function isSecondaryTag(tag) {
  return tag.position() === null || tag.position() === undefined;
}
function flarumTags(path) {
  try {
    return isTagsEnabled() ? flarum.reg.get('flarum-tags', path) : null;
  } catch {
    return null;
  }
}

/** `getSelectableTags` do flarum/tags (com cópia de reserva). */
export function selectableTags(discussion) {
  if (!isTagsEnabled()) return [];
  const own = flarumTags('forum/utils/getSelectableTags');
  if (typeof own === 'function') return own(discussion || undefined);
  const tags = allTags();
  if (discussion) {
    const current = discussion.tags?.() || [];
    return tags.filter(tag => tag.canAddToDiscussion?.() || current.includes(tag));
  }
  return tags.filter(tag => !!tag.canStartDiscussion?.());
}

/** A ordem do TagSelectionModal (`sortTags` do flarum/tags). */
export function sortSelectable(tags) {
  const own = flarumTags('common/utils/sortTags');
  return typeof own === 'function' ? own(tags) : themeSortTags(tags);
}
export function countTags(selected) {
  const primary = selected.filter(isPrimaryParent).length;
  return {
    primary,
    secondary: selected.length - primary
  };
}

/**
 * Tira o que o flarum/tags não aceitaria na seleção: repetidas, filhas cujo
 * pai saiu e, se `allowed` vier, as tags fora dele.
 */
export function normalizeTags(selected, allowed) {
  const result = [];
  for (const tag of selected) {
    if (!tag || result.includes(tag)) continue;
    if (allowed && !allowed.includes(tag)) continue;
    result.push(tag);
  }
  return result.filter(tag => {
    const parent = parentTag(tag);
    return !parent || result.includes(parent);
  });
}

/** Tira a tag e, como o modal do core, as filhas dela. */
export function removeTag(selected, tag) {
  return selected.filter(item => item !== tag && parentTag(item) !== tag);
}

/** Se ainda cabe mais uma tag desse tipo (os máximos valem para todos). */
export function hasRoomFor(tag, selected, limits = tagLimits()) {
  const {
    primary,
    secondary
  } = countTags(selected);
  return isPrimaryParent(tag) ? primary < limits.maxPrimary : secondary < limits.maxSecondary;
}

/**
 * Se a tag pode entrar agora: está entre as selecionáveis, o pai dela (se
 * for filha) já foi escolhido e o máximo do tipo dela não foi atingido.
 */
export function canAddTag(tag, selected, selectable = selectableTags(), limits = tagLimits()) {
  if (selected.includes(tag) || !selectable.includes(tag)) return false;
  const parent = parentTag(tag);
  if (parent && !selected.includes(parent)) return false;
  return hasRoomFor(tag, selected, limits);
}

/**
 * Acrescenta a tag respeitando as regras. Uma filha sugerida sem o pai leva o
 * pai junto (como o `addTag` do modal), se o pai couber. Devolve `null`
 * quando não dá para acrescentar.
 */
export function addTag(selected, tag, selectable = selectableTags(), limits = tagLimits()) {
  const parent = parentTag(tag);
  let base = selected;
  if (parent && !base.includes(parent)) {
    if (!canAddTag(parent, base, selectable, limits)) return null;
    base = base.concat(parent);
  }
  return canAddTag(tag, base, selectable, limits) ? base.concat(tag) : null;
}

/**
 * O que falta para publicar, com o texto do próprio flarum/tags; `null`
 * quando a seleção atende aos mínimos (ou o ator pode ignorá-los).
 */
export function missingTagsMessage(selected, limits = tagLimits()) {
  if (limits.bypass) return null;
  const {
    primary,
    secondary
  } = countTags(selected);
  if (primary < limits.minPrimary) {
    return {
      kind: 'primary',
      message: app.translator.trans('flarum-tags.lib.tag_selection_modal.choose_primary_placeholder', {
        count: limits.minPrimary - primary
      }, true)
    };
  }
  if (secondary < limits.minSecondary) {
    return {
      kind: 'secondary',
      message: app.translator.trans('flarum-tags.lib.tag_selection_modal.choose_secondary_placeholder', {
        count: limits.minSecondary - secondary
      }, true)
    };
  }
  return null;
}
flarum.reg.add('ramon-canela', 'forum/composer/tagRules', { tagLimits: tagLimits,parentTag: parentTag,isPrimaryParent: isPrimaryParent,isSecondaryTag: isSecondaryTag,selectableTags: selectableTags,sortSelectable: sortSelectable,countTags: countTags,normalizeTags: normalizeTags,removeTag: removeTag,hasRoomFor: hasRoomFor,canAddTag: canAddTag,addTag: addTag,missingTagsMessage: missingTagsMessage, });